import { cellText, coalesce, formatDate, isEmptyCell } from './helpers';
import { EQUIPMENT_TYPE_LABELS } from './constants';
import { formatDateForCsv } from './exportCsv';

const SNAKE_TO_CAMEL = {
  client_id: 'clientId',
  site_id: 'siteId',
  plan_id: 'planId',
  serial_number: 'serialNumber',
  extinguisher_type: 'extinguisherType',
  manufacturing_date: 'manufacturingDate',
  last_maintenance_date: 'lastMaintenanceDate',
  next_maintenance_date: 'nextMaintenanceDate',
  expiration_date: 'expirationDate',
  vehicle_registration: 'vehicleRegistration',
  vehicle_site: 'vehicleSite',
  physical_condition: 'physicalCondition',
  spare_parts: 'spareParts',
  first_aid_kit: 'firstAidKit',
  position_x: 'positionX',
  position_y: 'positionY',
  inventory_number: 'inventoryNumber',
  detailed_type: 'detailedType',
  vehicle_type: 'vehicleType',
  recovery_date: 'recoveryDate',
  recovered_by: 'recoveredBy',
  detailed_location: 'detailedLocation',
  created_at: 'createdAt',
  updated_at: 'updatedAt',
};

export function storedEquipmentStatusLabel(status) {
  if (status === 'operational') return 'Conforme';
  if (status === 'expired') return 'Expiré';
  if (status === 'needs_maintenance') return 'Maintenance requise';
  if (status === 'out_of_service') return 'Hors service';
  return cellText(status);
}

/** Single source of truth for labels when an equipment row appears anywhere. */
export const EQUIPMENT_FIELD_DEFS = [
  { key: 'clientName', label: 'Client' },
  { key: 'clientCompany', label: 'Société', altKeys: ['company'] },
  { key: 'siteName', label: 'Site' },
  { key: 'siteAddress', label: 'Adresse site' },
  { key: 'inventoryNumber', label: 'N° inventaire' },
  { key: 'serialNumber', label: 'N° série' },
  { key: 'type', label: 'Catégorie', format: 'equipmentType' },
  { key: 'extinguisherType', label: 'Type extincteur' },
  { key: 'detailedType', label: 'Type détaillé' },
  { key: 'capacity', label: 'Capacité' },
  { key: 'detailedLocation', label: 'Emplacement détaillé' },
  { key: 'location', label: 'Emplacement' },
  { key: 'structure', label: 'Structure' },
  { key: 'affectation', label: 'Affectation' },
  { key: 'vehicleType', label: 'Type véhicule' },
  { key: 'vehicleRegistration', label: 'Matricule véhicule' },
  { key: 'vehicleSite', label: 'Site véhicule' },
  { key: 'manufacturingDate', label: 'Date fabrication', format: 'date' },
  { key: 'lastMaintenanceDate', label: 'Date contrôle', format: 'date' },
  { key: 'nextMaintenanceDate', label: 'Prochaine vérification', format: 'date' },
  { key: 'expirationDate', label: 'Date recharge', format: 'date' },
  { key: 'recoveryDate', label: 'Date récupération', format: 'date' },
  { key: 'recoveredBy', label: 'Récupéré par' },
  { key: 'physicalCondition', label: 'Aspect physique' },
  { key: 'spareParts', label: 'Pièces détachées' },
  { key: 'firstAidKit', label: 'Boîte à pharmacie' },
  { key: 'observation', label: 'Observation' },
  { key: 'notes', label: 'Notes' },
  { key: 'status', label: 'Statut', format: 'storedStatus' },
  { key: 'positionX', label: 'Position X (plan)' },
  { key: 'positionY', label: 'Position Y (plan)' },
];

export function normalizeEquipmentRecord(raw = {}) {
  const out = { ...raw };
  for (const [snake, camel] of Object.entries(SNAKE_TO_CAMEL)) {
    if (out[camel] == null && raw[snake] != null) out[camel] = raw[snake];
    if (out[snake] == null && raw[camel] != null) out[snake] = raw[camel];
  }
  if (isEmptyCell(out.clientCompany)) {
    out.clientCompany = coalesce(raw.company, raw.client_company);
  }
  return out;
}

function readFieldValue(record, def) {
  let val = record[def.key];
  if (isEmptyCell(val) && def.altKeys) {
    for (const alt of def.altKeys) {
      val = coalesce(val, record[alt]);
      if (!isEmptyCell(val)) break;
    }
  }
  return val;
}

export function formatEquipmentFieldPlain(record, def) {
  const norm = normalizeEquipmentRecord(record);
  const val = readFieldValue(norm, def);
  if (def.format === 'date') {
    return isEmptyCell(val) ? cellText(val) : formatDateForCsv(val);
  }
  if (def.format === 'equipmentType') {
    return cellText(EQUIPMENT_TYPE_LABELS[val] || val);
  }
  if (def.format === 'storedStatus') {
    return storedEquipmentStatusLabel(val);
  }
  return cellText(val);
}

/** @returns {{ key: string, label: string, plain: string }[]} */
const REPORT_OMIT_WHEN_EMPTY_KEYS = new Set([
  'clientName',
  'clientCompany',
  'siteAddress',
  'positionX',
  'positionY',
]);

export function getEquipmentDisplayEntries(record, { includeEmpty = true, forReport = false } = {}) {
  const norm = normalizeEquipmentRecord(record);
  const entries = [];
  for (const def of EQUIPMENT_FIELD_DEFS) {
    if (forReport && REPORT_OMIT_WHEN_EMPTY_KEYS.has(def.key)) {
      const plain = formatEquipmentFieldPlain(norm, def);
      if (plain === cellText(null)) continue;
    }
    const plain = formatEquipmentFieldPlain(norm, def);
    if (!includeEmpty && plain === cellText(null)) continue;
    entries.push({ key: def.key, label: def.label, plain });
  }
  return entries;
}

export function getEquipmentExportHeaders({ includeRowNumber = true } = {}) {
  const headers = EQUIPMENT_FIELD_DEFS.map((d) => d.label);
  return includeRowNumber ? ['N°', ...headers] : headers;
}

export function getEquipmentExportRow(record, index = 0, { includeRowNumber = true } = {}) {
  const norm = normalizeEquipmentRecord(record);
  const cells = EQUIPMENT_FIELD_DEFS.map((def) => formatEquipmentFieldPlain(norm, def));
  const dateKeys = new Set(
    EQUIPMENT_FIELD_DEFS.filter((d) => d.format === 'date').map((d) => d.label)
  );
  const textColumnIndexes = [];
  const row = includeRowNumber ? [String(index + 1), ...cells] : cells;
  if (includeRowNumber) {
    EQUIPMENT_FIELD_DEFS.forEach((def, i) => {
      if (def.format === 'date') textColumnIndexes.push(i + 1);
    });
  } else {
    EQUIPMENT_FIELD_DEFS.forEach((def, i) => {
      if (def.format === 'date') textColumnIndexes.push(i);
    });
  }
  return { row, textColumnIndexes, dateLabels: dateKeys };
}

/** Draw one equipment block in jsPDF; returns new yPos. */
export function drawEquipmentDetailPdf(doc, record, index, startY, options = {}) {
  const { pageBreakAt = 270, indent = 20, titleIndent = 15 } = options;
  let yPos = startY;
  const norm = normalizeEquipmentRecord(record);
  const title = `${index + 1}. ${cellText(norm.serialNumber)}`;

  if (yPos > pageBreakAt - 10) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFont('helvetica', 'bold');
  doc.text(title, titleIndent, yPos);
  yPos += 5;
  doc.setFont('helvetica', 'normal');

  const entries = getEquipmentDisplayEntries(norm, { includeEmpty: false, forReport: true });
  for (const { label, plain } of entries) {
    if (yPos > pageBreakAt) {
      doc.addPage();
      yPos = 20;
    }
    const line = doc.splitTextToSize(`${label}: ${plain}`, 170);
    doc.text(line, indent, yPos);
    yPos += line.length * 4 + 1;
  }
  yPos += 3;
  return yPos;
}
